import { useRef, useState } from 'react';
import { Activity, ArrowUp, FileSpreadsheet, FileText, Folder, HardDrive, Link2, MessageSquare, Plus, RefreshCcw, Sparkles, X } from 'lucide-react';
import { activityCountFor, chatStoryFor, filesFor, linkedCountFor, pulsesFor } from '../../data/trackDetail';
import { GROUP_NAMES, TRACKS } from '../../data/workspaceTracks';
import { HUES } from '../alt/hues';
import { Glyph, Panel, Row, RowText } from '../dashboard/ui';
import { TrackActivityView, TrackLinkedView, TrackOverviewView } from './TrackDetailViews';
export type TrackSection = 'chat' | 'overview' | 'linked' | 'activity' | 'pulses' | 'drive';
type ThreadTab = {
  id: number;
  name: string;
  seeded: boolean;
};
const SECTIONS: Array<{
  id: TrackSection;
  name: string;
  icon: typeof MessageSquare;
}> = [{
  id: 'chat',
  name: 'Chat',
  icon: MessageSquare
}, {
  id: 'overview',
  name: 'Overview',
  icon: Folder
}, {
  id: 'linked',
  name: 'Linked',
  icon: Link2
}, {
  id: 'activity',
  name: 'Activity',
  icon: Activity
}, {
  id: 'pulses',
  name: 'Pulses',
  icon: RefreshCcw
}, {
  id: 'drive',
  name: 'Drive',
  icon: HardDrive
}];

/**
 * A TRACK IS A WORKSPACE, NOT A CHATBOX.
 *
 * This component deliberately has no card shell, radius, outer border, inset
 * margin, or drop shadow. It is rendered as a full-height sibling of the chat
 * and workspace views inside Giga Chat, so its boundaries are the browser
 * edges and the shell's own navigation rail. The first implementation put a
 * rounded app inside the app and made the conversation look like a widget.
 *
 * The supplied reference uses a saturated blue track bar. We keep its useful
 * information architecture but use the standard neutral product stack from
 * index.css (--app-* roles): white chrome, near-white canvas, neutral
 * hairlines, near-black type, underline selection. The brand accent marks the
 * active view and the send action, and nothing else.
 */
export function TrackWorkspace({
  trackName,
  initialSection = 'chat',
  staticSection,
  exportMode = false
}: {
  trackName: string;
  /** Initial tab for Screens and direct links; interactive after mount. */
  initialSection?: TrackSection;
  /** Locks the workspace to one finished state for PNG/SVG capture. */
  staticSection?: TrackSection;
  /** Uses a finite artboard height instead of the browser viewport. */
  exportMode?: boolean;
}) {
  const track = TRACKS.find(item => item.name === trackName);
  const group = track?.group ?? GROUP_NAMES[0];
  const paletteKeys = ['indigo', 'teal', 'clay', 'plum', 'sky', 'amber', 'moss'] as const;
  const groupIndex = Math.max(0, GROUP_NAMES.indexOf(group));
  const groupHue = HUES[paletteKeys[groupIndex % paletteKeys.length]];
  const story = chatStoryFor(trackName);
  const trackPulses = pulsesFor(trackName);
  const [interactiveSection, setInteractiveSection] = useState<TrackSection>(initialSection);
  const section = staticSection ?? interactiveSection;
  const [threads, setThreads] = useState<ThreadTab[]>(() => story.threads.map((name, index) => ({
    id: index + 1,
    name,
    seeded: index === 0
  })));
  const [activeThread, setActiveThread] = useState(1);
  const nextThreadId = useRef(story.threads.length + 1);
  const [draft, setDraft] = useState('');
  const active = threads.find(thread => thread.id === activeThread);
  const linkedCount = linkedCountFor(trackName);
  const activityCount = activityCountFor(trackName);
  const pulseCount = trackPulses.length;
  const addThread = () => {
    const id = nextThreadId.current++;
    setThreads(current => [...current, {
      id,
      name: 'New chat',
      seeded: false
    }]);
    setActiveThread(id);
    if (!staticSection) setInteractiveSection('chat');
  };
  const removeThread = (id: number) => {
    setThreads(current => {
      const index = current.findIndex(thread => thread.id === id);
      const next = current.filter(thread => thread.id !== id);
      if (activeThread === id) setActiveThread(next[index - 1]?.id ?? next[0]?.id ?? -1);
      return next;
    });
  };
  return <div className={`flex w-full flex-col bg-app-surface text-app ${exportMode ? 'min-h-[680px]' : 'min-h-screen'}`} data-id="element-3924" data-mp="ozjsclddecf6">
      {/* One product toolbar, not a marketing headline stacked over navigation.
       The track name and its views are peers on the same plane. Colours are
       the semantic --app-* roles, so dark mode is one remap in index.css. */}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-app bg-app-surface px-4 md:px-6" data-id="element-3925" data-mp="18qotrfw7otif">
        <div className="flex min-w-[180px] max-w-[280px] shrink-0 items-center gap-2.5 pr-3 md:border-r md:border-app" data-id="element-3926" data-mp="1cqfgn8imapho">
          <Folder size={15} className="shrink-0 text-app-subtle" data-id="element-3927" data-mp="ytacxf6dm2pl" />
          <h1 className="truncate text-[14px] font-semibold text-app" data-id="element-3928" data-mp="13ozf7q9s5xpl">{trackName}</h1>
        </div>
        <nav className="flex min-w-0 flex-1 self-stretch gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Track sections" data-id="element-3929" data-mp="3jgncxnwmbu9g">
          {SECTIONS.map(item => {
          const selected = item.id === section;
          const count = item.id === 'chat' ? threads.length : item.id === 'linked' ? linkedCount : item.id === 'activity' ? activityCount : item.id === 'pulses' ? pulseCount : null;
          return <button key={item.id} type="button" onClick={() => {
            if (!staticSection) setInteractiveSection(item.id);
          }} aria-pressed={selected} className={`relative flex shrink-0 items-center gap-1.5 px-3 text-[12.5px] font-medium transition-colors duration-200 ease-out after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 ${selected ? 'text-app after:bg-app-accent' : 'text-app-subtle after:bg-transparent hover:text-app'}`} data-id="element-3930" data-mp="3pvb0wtae4604">
                <item.icon size={13} strokeWidth={2} data-id="element-3931" data-mp="3271hfy0nfuc5" />
                {item.name}
                {count !== null && <span className={`text-[10.5px] tabular-nums ${selected ? 'text-app-muted' : 'text-app-subtle'}`} data-id="element-3932" data-mp="2eh77ppbmmlw4">{count}</span>}
              </button>;
        })}
        </nav>
        <span className="hidden shrink-0 items-center gap-1.5 text-[11.5px] font-medium text-app-muted lg:flex" data-id="element-3933" data-mp="dgsl0deg4zqc">
          <span className="size-1.5 rounded-full" style={{
          backgroundColor: groupHue.solid
        }} data-id="element-3934" data-mp="iebwx6no90f4" />
          {group}
        </span>
      </header>

      {/* Open conversations belong to the Track, not only to the Chat view.
       Keeping this strip mounted means the team can inspect Overview or
       Activity without losing the conversations already in progress. */}
      <div className="flex h-11 shrink-0 items-center gap-1 overflow-x-auto border-b border-app bg-app-canvas px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:px-6" role="tablist" aria-label="Open track conversations" data-id="element-3935" data-mp="3vlvppx0i39q8">
        {threads.map(thread => {
        const selected = section === 'chat' && thread.id === activeThread;
        const openThread = () => {
          setActiveThread(thread.id);
          if (!staticSection) setInteractiveSection('chat');
        };
        return <div key={thread.id} role="tab" tabIndex={0} aria-selected={selected} onClick={openThread} onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openThread();
          }
        }} className={`group flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 text-[12px] font-medium transition-colors duration-200 ease-out ${selected ? 'bg-app-surface text-app shadow-[0_1px_2px_rgba(20,20,24,0.08)] ring-1 ring-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-3936" data-mp="34edro3pntn6f">
              <span className="max-w-[160px] truncate" data-id="element-3937" data-mp="2gnhtmoum1vqq">{thread.name}</span>
              <button type="button" onClick={event => {
            event.stopPropagation();
            removeThread(thread.id);
          }} aria-label={`Close ${thread.name}`} className="text-app-subtle opacity-0 transition-[color,opacity] duration-150 ease-out hover:text-app group-hover:opacity-100 group-focus-within:opacity-100" data-id="element-3938" data-mp="2co0m7pvmioix">
                <X size={11} data-id="element-3939" data-mp="5ux3ojf50jij" />
              </button>
            </div>;
      })}
        <button type="button" onClick={addThread} aria-label="New chat in this track" className="ml-1 flex size-7 shrink-0 items-center justify-center rounded-lg text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-3940" data-mp="3axbetpibtxoc">
          <Plus size={14} data-id="element-3941" data-mp="365jnhcrcjnx5" />
        </button>
      </div>

      {section === 'chat' ? <div className="flex min-h-0 flex-1 flex-col" data-id="element-3942" data-mp="2s6bmcebmi5qb">
          {/* Thread and composer are centred for reading, while their parent is
           full-width. That distinction is the important correction: readable
           content measure does not mean the PRODUCT itself is a card. */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-app-canvas px-5 py-7 md:px-8" data-id="element-3943" data-mp="2iah1dcjxfykd">
            {active?.seeded ? <div className="mx-auto flex max-w-[720px] flex-col gap-5" data-id="element-3944" data-mp="ggrg2erunodd">
                <div className="flex justify-end" data-id="element-3945" data-mp="geohh6cmsmx1">
                  <div className="max-w-[82%] rounded-2xl rounded-tr-md bg-app-text px-4 py-3 text-[14px] leading-relaxed text-white" data-id="element-3946" data-mp="lg44h3ydd3i2">
                    {story.userPrompt}
                  </div>
                </div>
                <div data-id="element-3947" data-mp="2104ns8n734rt">
                  <div className="mb-2.5 flex items-center gap-1.5 text-[11.5px] font-medium text-app-subtle" data-id="element-3948" data-mp="210mrfqc6sl9r">
                    <Sparkles size={12} className="text-app-accent" data-id="element-3949" data-mp="2fpj09pm2i2bf" />
                    Worked for {story.workTime} · {story.toolCalls} tool calls
                  </div>
                  <div className="max-w-[86%] rounded-2xl rounded-tl-md border border-app bg-app-surface px-4 py-3 text-[14px] leading-relaxed text-app" data-id="element-3950" data-mp="2omtcpeamfxhx">
                    {story.assistantIntro}
                  </div>
                </div>
                <div className="max-w-[86%] border-l-2 border-app-accent pl-4" data-id="element-3951" data-mp="2tnhaqjx9peln">
                  <h2 className="text-[13.5px] font-semibold text-app" data-id="element-3952" data-mp="2tleg1ml66xal">{story.summaryTitle}</h2>
                  <ul className="mt-2 space-y-2 text-[13.5px] leading-relaxed text-app-muted" data-id="element-3953" data-mp="14and1f9cusj9">
                    {story.highlights.map(highlight => <li key={highlight} data-id="element-3954" data-mp="3tog1tkep39v4">{highlight}</li>)}
                  </ul>
                </div>
              </div> : <div className="mx-auto flex h-full max-w-[720px] flex-col items-center justify-center text-center" data-id="element-3955" data-mp="3op3ivaqy2mxt">
                <MessageSquare size={22} className="text-app-subtle" data-id="element-3956" data-mp="3jrl6199t3nkj" />
                <p className="mt-3 text-[13.5px] text-app-muted" data-id="element-3957" data-mp="3erp3legrc473">
                  New chat in {trackName}. Ask Giga to get something done.
                </p>
              </div>}
          </div>

          <div className="shrink-0 border-t border-app bg-app-surface px-5 py-3 md:px-8" data-id="element-3958" data-mp="bb076law2av5">
            <div className="mx-auto flex max-w-[720px] items-end gap-2 rounded-xl border border-app bg-app-surface px-3.5 py-2.5 shadow-[0_8px_30px_-20px_rgba(20,20,24,0.25)] focus-within:border-app-accent" data-id="element-3959" data-mp="6k08pbm4fkqj">
              <button type="button" aria-label="Add attachment" className="flex size-7 shrink-0 items-center justify-center text-app-subtle transition-colors duration-200 ease-out hover:text-app" data-id="element-3960" data-mp="z018zpp7unrg">
                <Plus size={16} data-id="element-3961" data-mp="12ii60gtk01g0" />
              </button>
              <textarea value={draft} onChange={event => setDraft(event.target.value)} rows={1} placeholder={`Message Giga about ${trackName}`} className="min-h-[24px] max-h-28 flex-1 resize-none bg-transparent text-[14px] text-app outline-none placeholder:text-app-subtle" data-id="element-3962" data-mp="12k1o8cyrsmkf" />
              <button type="button" aria-label="Send" disabled={!draft.trim()} className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-app-accent text-white transition-colors duration-200 ease-out enabled:hover:bg-[var(--app-accent-strong)] disabled:opacity-25" data-id="element-3963" data-mp="1gj1a5cuwencw">
                <ArrowUp size={15} data-id="element-3964" data-mp="1u94svtukjyxl" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[10.5px] text-app-subtle" data-id="element-3965" data-mp="1z8hob2ziaebs">
              Giga can make mistakes. Check important information.
            </p>
          </div>
        </div> : <TrackSectionContent section={section} trackName={trackName} group={group} groupHue={groupHue} data-id="element-3966" data-mp="245rckfb5sjy1" />}
    </div>;
}
function TrackSectionContent({
  section,
  trackName,
  group,
  groupHue
}: {
  section: Exclude<TrackSection, 'chat'>;
  trackName: string;
  group: string;
  groupHue: {
    solid: string;
    tint: string;
    ink: string;
  };
}) {
  const detailProps = {
    trackName,
    group,
    groupHue
  };
  return <div className="min-h-0 flex-1 overflow-y-auto bg-app-canvas px-5 py-8 md:px-8" data-id="element-3967" data-mp="1ohw8awvhw0ws">
      {section === 'overview' && <TrackOverviewView {...detailProps} data-id="element-3968" data-mp="22886ndioavwd" />}
      {section === 'linked' && <TrackLinkedView {...detailProps} data-id="element-3969" data-mp="1x9y7ysxqpd60" />}
      {section === 'activity' && <TrackActivityView {...detailProps} data-id="element-3970" data-mp="1x9oj58oflc1w" />}
      {section === 'pulses' && <TrackPulses trackName={trackName} data-id="element-3971" data-mp="2c65tbwkvwkmv" />}
      {section === 'drive' && <TrackDrive trackName={trackName} data-id="element-3972" data-mp="278dqi7ipbe3w" />}
    </div>;
}
function TrackPulses({
  trackName
}: {
  trackName: string;
}) {
  return <div className="mx-auto max-w-[900px]" data-id="element-3973" data-mp="hsed2e81v2ig">
      <div className="mb-4" data-id="element-3974" data-mp="hva9tz85vl5b">
        <h2 className="text-[15px] font-semibold text-app" data-id="element-3975" data-mp="cucstpyw0x5e">Work running on its own</h2>
        <p className="mt-1 text-[12.5px] text-app-muted" data-id="element-3976" data-mp="2ohg9wxbpbjv3">Giga watches this track and delivers updates before the team has to ask.</p>
      </div>
      <Panel data-id="element-3977" data-mp="2tfhvpq0ihxa9">
        {pulsesFor(trackName).map(pulse => <Row key={pulse.name} data-id="element-3978" data-mp="2ticgjinftk47">
            <Glyph icon={RefreshCcw} data-id="element-3979" data-mp="2eseub892j4hn" />
            <RowText title={pulse.name} meta={`${pulse.meta} · ${pulse.outcome}`} data-id="element-3980" data-mp="2erd94ayc91m7" />
            <span className={`shrink-0 rounded-md px-2 py-1 text-[10.5px] font-semibold ${pulse.status === 'Running' ? 'bg-app-accent-tint text-app-accent' : 'bg-app-subtle text-app-muted'}`} data-id="element-3981" data-mp="2jpezlgy3g37p">{pulse.status}</span>
          </Row>)}
      </Panel>
    </div>;
}
function TrackDrive({
  trackName
}: {
  trackName: string;
}) {
  return <div className="mx-auto max-w-[900px]" data-id="element-3982" data-mp="1vuluwfxnlcp0">
      <div className="mb-4" data-id="element-3983" data-mp="20rc88vi2pu6q">
        <h2 className="text-[15px] font-semibold text-app" data-id="element-3984" data-mp="20snb8t3p2xws">Files and deliverables</h2>
        <p className="mt-1 text-[12.5px] text-app-muted" data-id="element-3985" data-mp="3q7trac1a1wcr">Generated outputs and source files stay with the work that produced them.</p>
      </div>
      <Panel data-id="element-3986" data-mp="3v3sis8hwjuhe">
        {filesFor(trackName).map(file => <Row key={file.name} data-id="element-3987" data-mp="1v3vnmp5fhm7a">
            <Glyph icon={file.kind === 'spreadsheet' ? FileSpreadsheet : FileText} data-id="element-3988" data-mp="1v2ue4p4297ro" />
            <RowText title={file.name} meta={file.meta} data-id="element-3989" data-mp="1q5u5c6fttqwv" />
          </Row>)}
      </Panel>
    </div>;
}